import { Inject } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  effect,
} from '@angular/core';
import { SESSION_DATA_SOURCE, SessionDataSource } from '../../../core/services/session-data-source';
import {
  PatientTargetDataModel,
  PatientTargetModel,
} from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { Utils } from '../../../core/utils';

@Component({
  selector: 'app-box-target-intensity',
  templateUrl: './box-target-intensity.component.html',
  styleUrls: ['../data-entry-feedback.css'],
  standalone: false,
})
export class BoxTargetIntensityComponent extends BaseBoxComponent {
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;

  targetData: PatientTargetDataModel[] = [];
  lastIntensity: number | null = null;
  addingTargetData: boolean = false;
  lastRecordedIntensity: number | null = null;
  cooldownIntensity: number | null = null;
  text: string = '-';
  targetID: number = 0;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    @Inject(SESSION_DATA_SOURCE) private patientSessionService: SessionDataSource
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(this.targetID);
      if (targetData && targetData.length > 0) {
        this.targetData = targetData.filter((t) => !t.DeletedDate);
        this.targetData = this.targetData.filter(
          (value, index, self) =>
            index ===
            self.findIndex((t) => t.RecordedDate === value.RecordedDate)
        );
        const lastEntry = this.targetData[this.targetData.length - 1];
        this.lastIntensity = lastEntry?.IntValue ?? null;
      } else {
        this.targetData = [];
        this.lastIntensity = null;
      }
    });
  }

  ngOnInit(): void {
    this.targetID = this.target?.ID ?? 0;
    this.text = this.target?.TargetName ?? '-';
  }

  get intensityOptions(): number[] {
    return Utils.intensityScaleValues(
      this.target?.IntensityScaleMin,
      this.target?.IntensityScaleMax,
      this.target?.IntensityScaleValues
    );
  }

  setExtraBoxes(): void {
    const dropdowns = document.querySelectorAll('.p-overlay.p-component');
    this.extraBoxes = Array.from(dropdowns).map(
      (dropdown) => new ElementRef(dropdown)
    );
    if (this.extraBox) {
      this.extraBoxes.push(this.extraBox);
    }
  }

  override onClickOutside() {
    super.onClickOutside();
  }

  clickedBox(): void {
    this.showExtra();
    this.setExtraBoxes();
  }

  override doLongPress(): void {
    this.showExtra();
    this.setExtraBoxes();
  }

  selectIntensity(intensity: number): void {
    if (this.addingTargetData) {
      return;
    }
    this.addingTargetData = true;
    this.cooldownIntensity = intensity;

    const recordedDate = new Date()
      .toISOString()
      .slice(0, 19)
      .replace('T', ' ');
    const dateToSave = new Date(recordedDate).fromUTCtoServerString();

    const isDateInTargetData = this.targetData.some(
      (t) => t.RecordedDate === dateToSave
    );
    if (isDateInTargetData) {
      this.addingTargetData = false;
      this.cooldownIntensity = null;
      return;
    }

    this.patientSessionService.addTargetData({
      TextValue: intensity.toString(),
      IntValue: intensity,
      PatientTargetID: this.targetID,
      RecordedDate: dateToSave,
    });
    this.lastRecordedIntensity = intensity;
    setTimeout(() => {
      this.lastRecordedIntensity = null;
    }, 220);
    setTimeout(() => {
      this.addingTargetData = false;
      this.cooldownIntensity = null;
    }, 1000);
  }

  intensityColor(intensity: number): string {
    const options = this.intensityOptions;
    if (options.length <= 1) {
      return '#e53935';
    }
    const index = options.indexOf(intensity);
    const ratio = index / (options.length - 1);
    const hue = 120 - ratio * 120;
    return `hsl(${hue}, 65%, 42%)`;
  }
}
